/**
 * The organisation admin's switch for saved browser logins (AMO-152), on
 * Settings → General. Off stops every member's browser from keeping
 * logins across chats and forgets every saved login in the organisation, so
 * switching off asks once more. Members see the state but cannot change it;
 * nobody sees anyone else's sites here.
 *
 * Hidden entirely when the workspace server does not run saved logins.
 *
 * @module components/settings/BrowserLoginsOrganizationSection
 */
import { useEffect, useState } from "react";

import { fetchBrowserLogins, updateBrowserLoginOrganization } from "../../state/browserLogins";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { SettingsCard, SettingsCardRow } from "./settingsCard";
import { searchableSetting } from "./settingsSearch";

export function BrowserLoginsOrganizationSection({
  organizationId,
  organizationName,
  canManage,
}: {
  readonly organizationId: string;
  readonly organizationName: string;
  readonly canManage: boolean;
}) {
  const [orgOn, setOrgOn] = useState<boolean | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [confirmingOff, setConfirmingOff] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setOrgOn(null);
    setConfirmingOff(false);
    setError(null);
    void fetchBrowserLogins(organizationId).then((logins) => {
      if (cancelled || logins === null) return;
      setEnabled(logins.enabled);
      setOrgOn(logins.orgOn);
    });
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  if (!enabled || orgOn === null) return null;

  const save = async (on: boolean) => {
    setBusy(true);
    setError(null);
    const failure = await updateBrowserLoginOrganization(organizationId, on);
    setBusy(false);
    setConfirmingOff(false);
    if (failure) setError(failure);
    else setOrgOn(on);
  };

  return (
    <SettingsCard
      id="browser-logins-organization"
      title="Saved browser logins"
      description="Whether members stay signed into sites across their own chats."
    >
      <SettingsCardRow
        {...searchableSetting("browser-logins-organization")}
        description={
          canManage
            ? `Members of ${organizationName} can stay signed into sites across their own chats. Each person's logins are theirs alone; admins cannot see them. Turning this off forgets every member's saved logins.`
            : `Whether members of ${organizationName} can stay signed into sites across their own chats. Only organisation admins can change it.`
        }
        control={
          <Switch
            aria-label="Let members save browser logins"
            checked={orgOn}
            disabled={!canManage || busy || confirmingOff}
            onCheckedChange={(on) => (on ? void save(true) : setConfirmingOff(true))}
          />
        }
      />
      {confirmingOff ? (
        <SettingsCardRow
          id="browser-logins-organization-confirm"
          title="Turn off for everyone?"
          description="Every member's saved logins are forgotten now and their open chats are signed out of those sites. This cannot be undone."
          control={
            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => setConfirmingOff(false)}
              >
                Keep on
              </Button>
              <Button
                type="button"
                size="sm"
                variant="destructive"
                disabled={busy}
                onClick={() => void save(false)}
              >
                Turn off for everyone
              </Button>
            </div>
          }
        />
      ) : null}
      {error ? (
        <p role="alert" className="px-4 py-3 text-sm text-destructive sm:px-5">
          {error}
        </p>
      ) : null}
    </SettingsCard>
  );
}
